import { ReactElement } from 'react';
import { Redirect } from 'react-router';
import { Route } from 'react-router-dom';

import { ROOT_PATH, SecuritySwitch } from '@dreryk/egabinet-security-frontend';
import { isDemo } from '@dreryk/demo-variant-frontend';

import { protectFeature } from '~libs.shared.system';
import { useDashboardModuleRouter, useRedirectToDashboard } from '~legacy.dashboard';
import { PharmindexIntegrationPanel } from '~legacy.medicine-database-pharmindex';
import { useHealthcareProviderModuleRouter } from '~legacy.p1';
import { usePatientModuleRouter } from '~legacy.patient';
import { getMyAccountPath, MyAccountPanel, SchedulePanel, SchedulePanelLoader } from '~legacy.personnel';
import { PRIVATE_MEDICAL_SERVICE_PATH, PrivateMedicalServicePanel } from '~legacy.private-medical-service';
import { getStatisticsPath, StatisticsRouter } from '~legacy.statistics';
import { getGroupSessionsPath, GroupSessionsRouter } from '~libs.desktop.group-sessions';
import {
   ShowVisitButton,
   useAppointmentOutOfScheduleButton,
   useRedirectToAppointmentsList,
   useVisitModuleRouter,
   useVisitsHistoryPanel,
   VisitsHistoryButton,
} from '~legacy.visit';

import { PHARMINDEX_INTEGRATIONS_PATH } from './AppMainMenu';
import { AccountConfigurationVerificationEventInterceptor } from './features/accountConfigurationInfo';
import { useAccountConfigurationInfoModalTools } from './features/accountConfigurationInfo/accountConfigurationInfo.hooks';
import { usePractitionerAccountConfigurationInfoModalTools } from './features/accountConfigurationInfo/practitioner/practitionerAccountConfigurationInfo.hooks';
import { PaymentsAndInvoicesPanel } from './features/paymentsAndInvoices/PaymentsAndInvoicesPanel';
import { getPaymentsAndInvoicesPath } from './features/paymentsAndInvoices/PaymentsAndInvoicesPanel.utils';
import { TemplatesSettingsPanel } from './settings/TemplatesSettingsPanel';
import { SCHEDULE_PATH, SETTINGS_PATH, SYSTEM_ACTIVITY_PATH } from './toolbar/UserDropdown';
import { AppGlobalComponents } from './AppGlobalComponents';
import { SystemActivityPanel } from './systemActivity/SystemActivityPanel';

// eslint-disable-next-line max-lines-per-function
export const AppRoutes = (): ReactElement => {
   const { verifyAccountConfiguration } = useAccountConfigurationInfoModalTools();
   const { verifyPractitionerAccountConfiguration } = usePractitionerAccountConfigurationInfoModalTools();

   const visitModuleRouter = useVisitModuleRouter({
      accountConfigurationVerificationEventInterceptor: AccountConfigurationVerificationEventInterceptor,
   });

   const patientModuleRouter = usePatientModuleRouter({
      appointmentOutOfScheduleButton: useAppointmentOutOfScheduleButton(),
      visitHistoryPanel: useVisitsHistoryPanel(),
      accountConfigurationVerificationEventInterceptor: AccountConfigurationVerificationEventInterceptor,
      visitsHistoryButton: VisitsHistoryButton,
      showVisitButton: ShowVisitButton,
   });

   const dashboardModuleRouter = useDashboardModuleRouter();

   const healthcareProviderModuleRouter = useHealthcareProviderModuleRouter({
      verifyAccountConfiguration: verifyAccountConfiguration,
   });
   const redirectToAppointemntsList = useRedirectToAppointmentsList();
   const redirectToDashboard = useRedirectToDashboard();

   const accessToGroupSessions =
      protectFeature('conductingGroupSessions') ||
      protectFeature('managingGroupSessions') ||
      // TODO DREGAB-27876 Usunięcie flagi experimental
      protectFeature('experimental');

   return (
      <>
         <SecuritySwitch>
            <Route exact path={ROOT_PATH}>
               <SecuritySwitch>
                  {protectFeature('pharmindexIntegration', <Redirect to={PHARMINDEX_INTEGRATIONS_PATH} />)}
                  {redirectToDashboard}
                  {redirectToAppointemntsList}
               </SecuritySwitch>
            </Route>
            {dashboardModuleRouter}
            {visitModuleRouter}
            {patientModuleRouter}
            {protectFeature(
               ['searchingPrivateMedicalServices', 'creatingInvoices'],
               <Route path={getPaymentsAndInvoicesPath()}>
                  <PaymentsAndInvoicesPanel />
               </Route>
            )}
            {protectFeature(
               'creatingStatistics',
               <Route path={getStatisticsPath()}>
                  <StatisticsRouter />
               </Route>
            )}
            {accessToGroupSessions ? (
               <Route path={getGroupSessionsPath()}>
                  <GroupSessionsRouter />
               </Route>
            ) : null}
            <Route path={getMyAccountPath()}>
               <MyAccountPanel
                  verifyAccountConfiguration={async () => {
                     await Promise.all([verifyAccountConfiguration(), verifyPractitionerAccountConfiguration()]);
                  }}
               />
            </Route>
            {protectFeature(
               'creatingVisits',
               <Route path={SETTINGS_PATH}>
                  <TemplatesSettingsPanel />
               </Route>
            )}
            {isDemo()
               ? null
               : protectFeature(
                    ['updatingProviderData'],
                    <Route path={SYSTEM_ACTIVITY_PATH}>
                       <SystemActivityPanel />
                    </Route>,
                    null
                 )}

            {protectFeature(
               'updatingAvailability',
               <Route path={SCHEDULE_PATH}>
                  <SchedulePanelLoader>
                     {({ currentDoctor, doctors }) => <SchedulePanel defaultDoctor={currentDoctor} doctors={doctors} />}
                  </SchedulePanelLoader>
               </Route>
            )}
            {healthcareProviderModuleRouter}
            {protectFeature(
               'managingPrivateMedicalServices',
               <Route path={PRIVATE_MEDICAL_SERVICE_PATH}>
                  <PrivateMedicalServicePanel />
               </Route>
            )}
            {protectFeature(
               'pharmindexIntegration',
               <Route path={PHARMINDEX_INTEGRATIONS_PATH}>
                  <PharmindexIntegrationPanel />
               </Route>
            )}
         </SecuritySwitch>
         <AppGlobalComponents />
      </>
   );
};
